<template>
  <div class="min-h-screen bg-gray-50 py-16 md:py-24">
    <div class="container mx-auto px-4">
      
      <!-- Header Section -->
      <div class="text-center max-w-3xl mx-auto mb-20">
        <h1 class="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 relative inline-block tracking-tight">
          {{ $t('our_services') }}
          <span class="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1/2 h-1.5 bg-primary rounded-full opacity-80"></span>
        </h1>
        <p class="text-lg md:text-xl text-gray-600 leading-relaxed mb-10">
          {{ $t('cld_description1') }}
        </p>
        <NuxtLinkLocale :to="{ path: '/contact-us' }"
          class="inline-flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-full font-bold hover:bg-primary/90 transition-all shadow-xl transform hover:-translate-y-1 group">
          <span>{{ $t('contact_us') }}</span>
          <i class="fa fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
        </NuxtLinkLocale>
      </div>

      <!-- Services Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        
        <!-- Logistics -->
        <div class="bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden group">
          <div class="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-full -mr-20 -mt-20 transition-transform duration-500 opacity-50 group-hover:scale-150 group-hover:opacity-100"></div>
          
          <div class="relative z-10 h-full flex flex-col items-center text-center">
            <div class="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 bg-blue-50 text-blue-600">
              <i class="fa fa-truck text-3xl group-hover:animate-pulse"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{{ $t('logistics') }}</h3>
            <p class="text-gray-500 leading-relaxed text-sm">
              {{ $t('logistics_description') }}
            </p>
          </div>
        </div>

        <!-- Sales -->
        <div class="bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden group">
          <div class="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-full -mr-20 -mt-20 transition-transform duration-500 opacity-50 group-hover:scale-150 group-hover:opacity-100"></div>
          
          <div class="relative z-10 h-full flex flex-col items-center text-center">
             <div class="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 bg-emerald-50 text-emerald-600">
              <i class="fa fa-tags text-3xl group-hover:rotate-12 transition-transform duration-300"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{{ $t('sales') }}</h3>
            <p class="text-gray-500 leading-relaxed text-sm">
              {{ $t('sales_description') }}
            </p>
          </div>
        </div>

        <!-- Marketing -->
        <div class="bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden group">
          <div class="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-full -mr-20 -mt-20 transition-transform duration-500 opacity-50 group-hover:scale-150 group-hover:opacity-100"></div>
          
          <div class="relative z-10 h-full flex flex-col items-center text-center">
             <div class="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 bg-orange-50 text-orange-600">
              <i class="fa fa-bullhorn text-3xl group-hover:-rotate-12 transition-transform duration-300"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{{ $t('marketing') }}</h3>
            <p class="text-gray-500 leading-relaxed text-sm">
              {{ $t('marketing_description') }}
            </p>
          </div>
        </div>

        <!-- Integrations -->
        <div class="bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden group">
          <div class="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-full -mr-20 -mt-20 transition-transform duration-500 opacity-50 group-hover:scale-150 group-hover:opacity-100"></div>
          
          <div class="relative z-10 h-full flex flex-col items-center text-center">
             <div class="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 bg-purple-50 text-purple-600">
              <!-- Custom Gear Animation -->
              <div class="relative w-8 h-8 flex items-center justify-center">
                 <i class="fa fa-cog absolute -top-1 -right-1 text-xl opacity-80 animate-[spin_4s_linear_infinite]"></i>
                 <i class="fa fa-cog absolute -bottom-1 -left-1 text-2xl animate-[spin_5s_linear_infinite_reverse]"></i>
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{{ $t('integrations') }}</h3>
            <p class="text-gray-500 leading-relaxed text-sm">
              {{ $t('integrations_description') }}
            </p>
          </div>
        </div>

        <!-- Digital & AI services -->
        <div class="bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden group">
          <div class="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-full -mr-20 -mt-20 transition-transform duration-500 opacity-50 group-hover:scale-150 group-hover:opacity-100"></div>
          <div class="relative z-10 h-full flex flex-col items-center text-center">
            <div class="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 bg-teal-50 text-teal-600">
              <i class="fa fa-magic text-3xl group-hover:animate-pulse"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{{ $t('svc_digital_title') }}</h3>
            <p class="text-gray-500 leading-relaxed text-sm">
              {{ $t('svc_digital_text') }}
            </p>
          </div>
        </div>

        <!-- Fulfilment -->
        <div class="bg-white rounded-2xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden group">
          <div class="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-bl-full -mr-20 -mt-20 transition-transform duration-500 opacity-50 group-hover:scale-150 group-hover:opacity-100"></div>
          <div class="relative z-10 h-full flex flex-col items-center text-center">
            <div class="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-300 bg-rose-50 text-rose-600">
              <i class="fa fa-cubes text-3xl group-hover:-rotate-6 transition-transform duration-300"></i>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{{ $t('svc_fulfilment_title') }}</h3>
            <p class="text-gray-500 leading-relaxed text-sm">
              {{ $t('svc_fulfilment_text') }}
''            </p>
          </div>
        </div>

      </div>

      <!-- Local entities across Europe -->
      <div class="mt-16 bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm text-center">
        <h2 class="text-2xl font-bold text-gray-900 mb-3">{{ $t('svc_local_title') }}</h2>
        <p class="text-gray-500 max-w-3xl mx-auto mb-6">{{ $t('svc_local_text') }}</p>
        <div class="flex flex-wrap justify-center gap-2">
          <span v-for="entity in ['CLD Belux', 'CLD France', 'CLD Deutschland', 'CLD Ibérica', 'CLD Italy', 'CLD North East Europe']"
            :key="entity"
            class="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold">
            {{ entity }}
          </span>
        </div>
      </div>

      <!-- Partnerships -->
      <div class="mt-8 bg-white rounded-2xl p-8 md:p-10 border border-gray-100 shadow-sm text-center">
        <h2 class="text-2xl font-bold text-gray-900 mb-3">{{ $t('svc_partners_title') }}</h2>
        <p class="text-gray-500 max-w-3xl mx-auto">{{ $t('svc_partners_text') }}</p>
      </div>
    </div>
  </div>
</template>
<script setup>

const { t } = useI18n();

const route = useRoute();
const ogUrl = computed(() => `https://cld.eu${route.fullPath}`);

useHead({
  title: t('seo.seo_title_services'),
  link: [{ rel: 'canonical', href: ogUrl.value }],
  meta: [
    {
      property: 'og:title',
      content: t('seo.seo_title_services'),
    },
    {
      property: 'og:description',
      content: t('seo.seo_description_services'),
    },
    {
      property: 'og:url',
      content: ogUrl.value,
    },
    {
      name: 'description',
      content: t('seo.seo_description_services'),
    },
    {
      property: "og:type",
      content: "website"
    },
    {
      property: "og:image",
      content: ""
    },
    {
      property: "og:site_name",
      content: "CLD Distribution"
    },

  ],
});
</script>